import cloneDeep from 'lodash/fp/cloneDeep';
import FileSaver from 'file-saver';
import leadService from '../service/leadService';
import log from '../service/log';
import variants from '@/utils/enums/alertVariants';
import i18n from '@/i18n';

const initialState = {
  leadDetails: {},
  leadBusinessUserId: null,
  leadParentBusinessUserId: null,
  leadSourcesFilterQuery: {},
  leadNotFound: false,
  leadSoftDeleted: false,
};

const getters = {
  getLeadFilterQuery: state => state.leadSourcesFilterQuery,
};

const actions = {
  async getLeadDetails({ commit }, leadId) {
    try {
      // const res = { data: require('@/mock/leadDetails2.json') };
      // const res = { data: require('@/mock/leadDetailsCIM.json') };
      // const res = { data: require('@/mock/leadDetailsCreditPlus.json') };
      // const res = { data: require('@/mock/leadDetailsEcomV2.json') };
      // const res = { data: require('@/mock/leadDetailsEquifax.json') };
      // const res = { data: require('@/mock/leadDetailsEquifax2.json') };
      // const res = { data: require('@/mock/leadDetailsEquifax3.json') };
      // const res = { data: require('@/mock/leadDetailsEquifax4.json') };
      // const res = { data: require('@/mock/leadDetailsFinance.json') };
      // const res = { data: require('@/mock/leadDetailsRegionalCredit.json') };
      // const res = { data: require('@/mock/leadDetailsRFA.json') };
      // const res = { data: require('@/mock/leadDetailsRFAFinanceLead.json') };
      // const res = { data: require('@/mock/leadDetailsRFAInitialLead.json') };
      // const res = { data: require('@/mock/leadDetailsTrade.json') };
      // const res = { data: require('@/mock/leadDetailsAI.json') };
      // const res = { data: require('@/mock/leadDetailsIDV.json') };
      // const res = { data: require('@/mock/leadDetailsUSCredit.json') };
      // const res = { data: require('@/mock/leadDetailsPrequalExpired.json') };
      const res = await leadService.getSingleLeadDetails(leadId);
      commit('SET_LEAD_NOT_FOUND', false);
      commit('SET_LEAD_SOFT_DELETED', false);
      commit('SET_LEAD_DETAILS', res.data.lead);
      if (res.data.businessUserId) {
        commit('SET_LEAD_BUSINESS_USER_ID', res.data.businessUserId);
      }
      if (res.data.parentBusinessUserId) {
        commit('SET_LEAD_PARENT_BUSINESS_USER_ID', res.data.parentBusinessUserId);
      }
      return true;
    } catch (e) {
      const status = e?.response?.status;
      const isSoftDeleted = e?.response?.data?.isSoftDeleted;
      commit('SET_LEAD_NOT_FOUND', true);
      commit('SET_LEAD_SOFT_DELETED', !!isSoftDeleted);
      // 404 means the lead does not exist (deleted or invalid ID)
      // 422 means validation failed, both are user-sided and do not need to be reported to bugsnag
      if (status !== 404 && status !== 422) {
        log(e);
      }
      return false;
    }
  },
  resetLeadDetails({ commit }) {
    commit('SET_LEAD_DETAILS', {});
    commit('SET_LEAD_NOT_FOUND', false);
    commit('SET_LEAD_SOFT_DELETED', false);
  },
  async fetchAndDownloadLeads({ commit }, exportOptions) {
    let fileName = 'AutoVerify-leads-';
    const { leadQueryParameters } = exportOptions;
    if (leadQueryParameters && leadQueryParameters.toDate && leadQueryParameters.fromDate) {
      fileName += `${leadQueryParameters.fromDate}-to-${leadQueryParameters.toDate}`;
    } else {
      const currentDate = new Date();
      fileName += `${currentDate.getFullYear()}-${currentDate.getMonth() + 1}-${currentDate.getDate()}`;
    }
    fileName += '.csv';
    await leadService
      .fetchAndDownloadLeads(exportOptions.leadType, exportOptions.leadQueryParameters, exportOptions.integrationId)
      .then(res => {
        const blob = new Blob([res.data], { type: 'text/csv' });
        FileSaver.saveAs(blob, fileName);
      })
      .catch(e => {
        commit(
          'SET_MESSAGE',
          { variant: variants.DANGER, message: { msg1: 'Error occurred while exporting leads.' } },
          { root: true }
        );
        log(e);
      });
  },
  async resendLeadEmails({ commit }, leadId) {
    try {
      const res = await leadService.resendLeadEmails(leadId);
      commit('SET_LEAD_LAST_RESENT', res.data.emailLastResent);
      commit(
        'SET_MESSAGE',
        { variant: variants.SUCCESS, message: { msg1: i18n.global.t('leadResent.successMsg') } },
        { root: true }
      );
      return true;
    } catch (e) {
      commit(
        'SET_MESSAGE',
        { variant: variants.DANGER, message: { msg1: i18n.global.t('alerts.generalError') } },
        { root: true }
      );
      log(e);
      return false;
    }
  },
  async softDeleteLead({ commit }, leadId) {
    try {
      await leadService.softDeleteLeads({ mode: 'include', leadIds: [leadId] });
      // Clear cache to ensure no stale data in any filter combination
      leadService.clearLeadsCache();
      return true;
    } catch (e) {
      commit(
        'SET_MESSAGE',
        {
          variant: variants.DANGER,
          message: { msg1: i18n.global.t('alerts.generalError'), msgHasLink: false },
        },
        { root: true }
      );
      log(e);
      return false;
    }
  },
};

const mutations = {
  SET_LEADS: (state, data) => {
    state.leads = data;
  },
  SET_TOTAL_LEAD_COUNT: (state, data) => {
    state.totalLeads = data;
  },
  SET_LEAD_DETAILS: (state, data) => {
    state.leadDetails = data;
  },
  SET_LEAD_NOT_FOUND: (state, data) => {
    state.leadNotFound = data;
  },
  SET_LEAD_SOFT_DELETED: (state, data) => {
    state.leadSoftDeleted = data;
  },
  SET_LEAD_LAST_RESENT: (state, data) => {
    state.leadDetails.lastResentAt = data;
  },
  // this one is really only used for testing, vue test utils complains so this is needed
  SET_LEAD_DETAILS_SSN_INFO: (state, data) => {
    state.leadDetails.financeInfo.SSNInfo = data;
  },
  SET_LEAD_BUSINESS_USER_ID: (state, data) => {
    state.leadBusinessUserId = data;
  },
  SET_LEAD_PARENT_BUSINESS_USER_ID: (state, data) => {
    state.leadParentBusinessUserId = data;
  },
  SET_LEAD_FILTER_QUERY(state, query) {
    state.leadSourcesFilterQuery = query;
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  getters,
  actions,
  mutations,
};
